import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Loader2, CheckCircle2 } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      setError(err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-am-bg px-5 py-10">
      <div className="pointer-events-none absolute left-1/2 top-[-10%] h-[460px] w-[460px] -translate-x-1/2 aura-radial opacity-[0.16] blur-3xl" />
      <div className="relative w-full max-w-[400px]">
        <button onClick={() => navigate("/")} className="mx-auto mb-6 flex flex-col items-center gap-3">
          <Logo size={60} />
          <span className="font-mark text-xl font-extrabold tracking-tight text-white">AURAMAX</span>
        </button>

        <div className="rounded-3xl border border-am-border bg-am-surface/60 p-6 backdrop-blur-xl sm:p-7">
          {done ? (
            <div className="text-center" data-testid="reset-done">
              <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl aura-gradient"><CheckCircle2 className="h-7 w-7 text-black" /></div>
              <h1 className="font-display text-2xl font-extrabold text-white">Password updated</h1>
              <p className="mt-2 text-sm text-am-text2">Taking you to log in…</p>
            </div>
          ) : !token ? (
            <div className="text-center">
              <h1 className="font-display text-2xl font-extrabold text-white">Invalid link</h1>
              <p className="mt-2 text-sm text-am-text2">This reset link is missing or broken. Request a new one.</p>
              <button onClick={() => navigate("/forgot")} className="mt-6 w-full rounded-full bg-white py-3 text-sm font-bold text-black">Request new link</button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-extrabold text-white">Choose a new password</h1>
              <p className="mt-1 text-sm text-am-text2">Make it strong. You'll use this to log in.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
                  <Lock className="h-4 w-4 text-am-text3" />
                  <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={6} data-testid="reset-password" placeholder="New password" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
                </div>
                <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
                  <Lock className="h-4 w-4 text-am-text3" />
                  <input value={confirm} onChange={(e) => setConfirm(e.target.value)} type="password" required data-testid="reset-confirm" placeholder="Confirm new password" className="w-full bg-transparent px-3 py-3 text-sm text-white placeholder:text-am-text3 outline-none" />
                </div>
                {error && <p data-testid="reset-error" className="rounded-lg bg-aura-magenta/10 px-3 py-2 text-xs text-aura-magenta">{error}</p>}
                <button type="submit" disabled={busy} data-testid="reset-submit" className="flex w-full items-center justify-center gap-2 rounded-full p-[1.5px] aura-gradient disabled:opacity-60">
                  <span className="flex w-full items-center justify-center gap-2 rounded-full bg-am-bg py-3 text-sm font-bold text-white">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
                  </span>
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
